import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, MessageCircle, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { CtaBand } from "@/components/CtaBand";
import { BUSINESS, telLink, mailLink, whatsappLink } from "@/data/business";
import { container, sectionPad, btnPrimary, cardShadow } from "@/lib/ui";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";

export default function Contact() {
  return (
    <>
      <Seo
        title="Contact — House Cleaners Melbourne"
        description="Contact JK House Cleaning in Melbourne. Call 0427 510 406, email or message us on WhatsApp, or request a free quote online. Mon–Fri 9:00 AM – 4:30 PM."
        path="/contact"
        schema={[localBusinessSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }])]}
      />
      <PageHeader
        eyebrow="Contact"
        title="Let's get your home sparkling"
        subtitle="Call, email or WhatsApp us — or request a free quote and we'll get back to you the same business day."
        crumbs={[{ name: "Contact" }]}
      />

      <section className={`${sectionPad} bg-white`}>
        <div className={`${container} grid lg:grid-cols-2 gap-10`}>
          <div className="space-y-4">
            <a href={telLink} className={`flex items-center gap-4 rounded-2xl bg-neutralbg/70 border border-gray-100 p-6 transition-transform hover:-translate-y-1 ${cardShadow}`} data-testid="contact-call">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-brandgreen/10"><Phone className="w-6 h-6 text-brandgreen" /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-gray-400 font-semibold">Call us</span>
                <span className="block font-heading text-lg font-semibold text-navy">{BUSINESS.phoneDisplay}</span>
              </span>
            </a>
            <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-4 rounded-2xl bg-neutralbg/70 border border-gray-100 p-6 transition-transform hover:-translate-y-1 ${cardShadow}`} data-testid="contact-whatsapp">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-brandgreen/10"><MessageCircle className="w-6 h-6 text-brandgreen" /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-gray-400 font-semibold">WhatsApp</span>
                <span className="block font-heading text-lg font-semibold text-navy">Message us instantly</span>
              </span>
            </a>
            <a href={mailLink} className={`flex items-center gap-4 rounded-2xl bg-neutralbg/70 border border-gray-100 p-6 transition-transform hover:-translate-y-1 ${cardShadow}`} data-testid="contact-email">
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-brandgreen/10"><Mail className="w-6 h-6 text-brandgreen" /></span>
              <span className="min-w-0">
                <span className="block text-xs uppercase tracking-wider text-gray-400 font-semibold">Email</span>
                <span className="block font-heading text-lg font-semibold text-navy break-all">{BUSINESS.email}</span>
              </span>
            </a>
            <div className={`flex items-center gap-4 rounded-2xl bg-neutralbg/70 border border-gray-100 p-6 ${cardShadow}`}>
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-brandgreen/10"><MapPin className="w-6 h-6 text-brandgreen" /></span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-gray-400 font-semibold">Service Areas</span>
                <span className="block font-heading text-lg font-semibold text-navy">West Melbourne · East Melbourne · North Melbourne</span>
              </span>
            </div>
          </div>

          <div className={`rounded-3xl bg-navy p-8 text-white ${cardShadow}`}>
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-brandgreen-light" />
              <h2 className="font-heading text-xl font-bold">Opening Hours</h2>
            </div>
            <ul className="mt-6 divide-y divide-white/10">
              {BUSINESS.hours.map((h) => (
                <li key={h.day} className="flex items-center justify-between py-3">
                  <span className="text-white/85">{h.day}</span>
                  <span className={`text-sm font-medium ${h.open ? "text-brandgreen-light" : "text-white/50"}`}>{h.time}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-xl bg-white/5 p-4 text-sm text-white/70">
              <p><strong className="text-white">Same-day cleaning</strong> available depending on availability.</p>
              <p className="mt-1"><strong className="text-white">Emergency cleaning</strong> sometimes available depending on schedule.</p>
            </div>
            <Link to="/quote" className={`${btnPrimary} w-full mt-6`} data-testid="contact-quote">
              Request a Free Quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
